import type { AdminDailyStats, AdminDailyStatsMetric } from '../services/api';

export type DailyPeriod = 7 | 14 | 30;
export const DAILY_PERIODS: DailyPeriod[] = [7, 14, 30];

export type PeriodSummary = {
  dates: string[];
  values: number[];
  /** Flow: the period's sum. Balance: the value on its last day. */
  total: number;
  previousTotal: number;
  /** Change against the previous period in percent; null when that was 0. */
  changePercent: number | null;
};

/** The chosen period's days and its KPI against the previous period of the same length. */
export function summarizePeriod(
  stats: Pick<AdminDailyStats, 'dates'>,
  metric: AdminDailyStatsMetric,
  days: DailyPeriod
): PeriodSummary {
  const length = Math.min(metric.values.length, stats.dates.length);
  const values = metric.values.slice(Math.max(length - days, 0), length);
  const previous = metric.values.slice(Math.max(length - 2 * days, 0), Math.max(length - days, 0));
  const dates = stats.dates.slice(Math.max(length - days, 0), length);
  const kpi = (series: number[]) =>
    metric.kind === 'balance' ? series[series.length - 1] ?? 0 : series.reduce((sum, value) => sum + value, 0);
  const total = kpi(values);
  const previousTotal = kpi(previous);
  return {
    dates,
    values,
    total,
    previousTotal,
    changePercent: previousTotal === 0 ? null : ((total - previousTotal) / Math.abs(previousTotal)) * 100
  };
}

/** A round axis maximum (1, 2, 2.5, 4, 5, 8 or 10 × a power of ten). */
export function niceMax(max: number, isCount: boolean): number {
  if (max <= 0) return isCount ? 4 : 1000;
  if (isCount && max <= 20) return Math.ceil(max / 4) * 4;
  const exponent = Math.pow(10, Math.floor(Math.log10(max)));
  const fraction = max / exponent;
  const step = [1, 2, 2.5, 4, 5, 8, 10].find((candidate) => fraction <= candidate) ?? 10;
  return step * exponent;
}

/** A smooth line through the points (Catmull-Rom as cubic Béziers), kept inside 0–height. */
export function smoothPath(points: Array<[number, number]>, height: number): string {
  if (points.length === 0) return '';
  const clamp = (y: number) => Math.max(0, Math.min(height, y));
  const fixed = (n: number) => n.toFixed(1);
  let path = `M${fixed(points[0][0])},${fixed(points[0][1])}`;
  if (points.length === 1) return `${path} L${fixed(points[0][0] + 1)},${fixed(points[0][1])}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = clamp(p1[1] + (p2[1] - p0[1]) / 6);
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = clamp(p2[1] - (p3[1] - p1[1]) / 6);
    path += ` C${fixed(c1x)},${fixed(c1y)} ${fixed(c2x)},${fixed(c2y)} ${fixed(p2[0])},${fixed(p2[1])}`;
  }
  return path;
}

/** A YYYY-MM-DD day as a local date, without a time zone shift. */
export function parseDay(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date);
}
